import Dexie from 'dexie';

const dbCache = new Map();
const SITE_KEY = process.env.REACT_APP_SITE_KEY;

/**
 * DATABASE FACTORY
 * Isolation is maintained by siteKey.
 */
export const getDB = (siteKey) => {
    const activeKey = siteKey || SITE_KEY;
    if (!activeKey) throw new Error("SiteKey is required for storage isolation.");

    if (dbCache.has(activeKey)) return dbCache.get(activeKey);

    const db = new Dexie(`SustinaCourier_${activeKey}`);

    /**
     * 🎯 SCHEMA PROTECTION:
     * We explicitly index S3CorsValue because it is mandatory for security. 
     * Other taxonomy fields are stored dynamically in the record.
     */
    db.version(1).stores({
        batches: '++id, batchId, status, timestamp, hash, bffTimestamp, siteKey, S3CorsValue, targetBucket, targetRoot',
        files: '++id, batchId, name, status, [batchId+name]'
    });

    dbCache.set(activeKey, db);
    return db;
};

/**
 * STAGE FILES LOCALLY
 */
export const stageFilesLocally = async (files, realBatchId, siteKey) => {
    const db = getDB(siteKey);
    const fileEntries = files.map(file => ({
        batchId: realBatchId,
        name: file.name,
        status: 'Staged',
        totalSize: file.size,
        bits: file, 
        timestamp: Date.now()
    }));

    return await db.files.bulkPut(fileEntries);
};

/**
 * FINALIZE LOCAL BATCH
 * 🎯 PRESERVATION LOGIC:
 * Explicitly captures S3CorsValue while preserving the rest of the 
 * Taxonomy object (more/less/none) via the spread operator.
 */
export const finalizeLocalBatch = async (realBatchId, siteKey, hash, timestamp, metadata = {}) => {
    const db = getDB(siteKey);
    return await db.transaction('rw', db.batches, db.files, async () => {
        const existing = await db.batches.where("batchId").equals(realBatchId).first();

        const batchData = {
            batchId: realBatchId,
            status: 'active',
            hash: hash,
            timestamp: timestamp,
            siteKey: siteKey || SITE_KEY,
            lastUpdated: Date.now(),
            // 🛡️ MANDATORY COLUMN
            S3CorsValue: metadata.S3CorsValue || "esg",
            // 📦 THE TAXONOMY OBJECT: 
            // We spread the entire object to ensure versionLabel, reportingYear, 
            // and any future fields are preserved through the pipeline.
            ...metadata 
        };

        if (!existing) {
            await db.batches.add(batchData);
        } else {
            await db.batches.update(existing.id, batchData);
        }

        // 🎯 DOWNSTREAM READINESS: 
        // Stamp individual files with the full metadata object. 
        // This allows Action 3 (S3 PUT) to pull labels JIT.
        await db.files.where('batchId').equals(realBatchId).modify({ 
            status: 'finalized',
            ...metadata 
        });
    });
};